import * as stylex from "@stylexjs/stylex";
import { X } from "lucide-react";
import { useRef } from "react";
import { copy } from "@/copy";
import "@/theme/tokens.stylex";
import { colors, motion, radii, shadows, space } from "@/theme/tokens.stylex";
import { Icon } from "@/ui/Icon";
import { press } from "@/ui/press";
import { useFocusWhenShown } from "./focusWhenShown";
import type { View } from "./routes";
import { SIDEBAR_PINNED, SIDEBAR_VIEWS, type SidebarItem } from "./sidebarItems";
type SidebarDrawerProps = {
  open: boolean;
  small?: boolean;
  active?: View;
  onClose: () => void;
  onScrimTap?: () => void;
  onNavigate: (path: string) => void;
};
const styles = {
  row: {
    kmuXW: "x2lah0s",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xddi9bj",
    kZKoxP: "x1peatla",
    k8WAf4: "xt970qd",
    kg3NbH: "xg4dor2",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1a11ngh",
    kMv6JI: "xjb2p0i",
    kGuDYH: "xwsyq91",
    k63SB2: "x1cf7cre",
    kb6lSQ: "x1yrajzq",
    k9WMMc: "xdpxx8g",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x18dpk69",
    kIyJzY: "xlc9y7w",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  smallRow: {
    kZKoxP: "xsdox4t",
    kOIVth: "x1hu4l6k",
    kg3NbH: "xw3ndq7",
    kGuDYH: "x1j61zf2",
    $$css: true
  },
  current: {
    kWkggS: "xfenp3h",
    kMwMTN: "xnnvkky",
    $$css: true
  }
};
export function SidebarDrawer({
  open,
  small = false,
  active,
  onClose,
  onScrimTap,
  onNavigate
}: SidebarDrawerProps) {
  const currentRow = useRef<HTMLButtonElement>(null);
  const iconSize = small ? 20 : 24;
  useFocusWhenShown(open, currentRow);
  const row = (item: SidebarItem) => {
    const current = item.key === active;
    return <button key={item.key} ref={current ? currentRow : undefined} type="button" aria-label={item.name === item.label ? undefined : item.name} aria-current={current ? "page" : undefined} onClick={() => onNavigate(item.path)} {...stylex.props(styles.row, small && styles.smallRow, current && styles.current, press.scale)}>
        <Icon icon={item.icon} size={iconSize} />
        <span>{item.label}</span>
      </button>;
  };
  return <div className="xixxii4 x10a8y8t xoegz02 x47corl">
      {small && open && <button type="button" tabIndex={-1} aria-hidden="true" onClick={onScrimTap} className="x10l6tqk x10a8y8t xc342km x1717udv x1ebrkjs x67bb7w" />}
      <nav aria-label={copy.nav.main} inert={!open} {...{
      0: {
        className: "x10l6tqk x1tk7jg1 xn0vg7t xpnmzw7 x1m258z3 x9f619 x1cnzs8 xw3ndq7 x2xw44r xh540kj xwe7p7i xnfxhx2 x67bb7w x78zum5 xdt5ytf xt4j9g6 xb3r6kr x11xpdln x1fmhw06 x18l85"
      },
      2: {
        className: "x10l6tqk x1tk7jg1 xn0vg7t xpnmzw7 x9f619 xh540kj xwe7p7i xnfxhx2 x67bb7w x78zum5 xdt5ytf xt4j9g6 xb3r6kr x11xpdln x1fmhw06 x18l85 x18qqrc1 x7beud5 x194p7y6 x141a865"
      },
      1: {
        className: "x10l6tqk x1tk7jg1 xn0vg7t xpnmzw7 x1m258z3 x9f619 x1cnzs8 xw3ndq7 x2xw44r xh540kj xwe7p7i xnfxhx2 x78zum5 xdt5ytf xt4j9g6 xb3r6kr x11xpdln x1fmhw06 x18l85 x1x0v3av x47corl"
      },
      3: {
        className: "x10l6tqk x1tk7jg1 xn0vg7t xpnmzw7 x9f619 xh540kj xwe7p7i xnfxhx2 x78zum5 xdt5ytf xt4j9g6 xb3r6kr x11xpdln x1fmhw06 x18l85 x18qqrc1 x7beud5 x194p7y6 x141a865 x1x0v3av x47corl"
      }
    }[!!small << 1 | !!!open << 0]}>
        <div className="x1iyjqo2 x2lwn1j xysyzu8 x78zum5 xdt5ytf xt4j9g6">
          <span {...{
          0: {
            className: "x2lah0s xexx8yu x4ud0wb x141a865 x1ibdujs x1pvqxga xfbjfs5 x1tup8yo x16bu0fj"
          },
          1: {
            className: "x2lah0s xexx8yu x4ud0wb xfbjfs5 x1tup8yo xzobpsk xfhybnk xwsyq91 x1h50m6h"
          }
        }[!!small << 0]}>{copy.appName}</span>
          {SIDEBAR_VIEWS.map(row)}
          <span aria-hidden="true" className="x1iyjqo2 x1hshjfz" />
          <span aria-hidden="true" {...{
          0: {
            className: "xjm9jq1 x2lah0s xdj266r xyktu5z x1vpiyjs x1wf3224"
          },
          1: {
            className: "xjm9jq1 x2lah0s xdj266r x1vpiyjs x1wf3224 xoa3ug4"
          }
        }[!!small << 0]} />
          {SIDEBAR_PINNED.map(row)}
        </div>
        <button type="button" aria-label={copy.nav.closeSidebar} onClick={onClose} {...stylex.props(styles.row, small && styles.smallRow, press.scale)}>
          <Icon icon={X} size={iconSize} />
          <span>{copy.nav.close}</span>
        </button>
      </nav>
    </div>;
}